// MuseIQ - State Controller
// ReactiveController for Lit components to subscribe to state events

import { ReactiveController, ReactiveControllerHost } from 'lit';

interface Subscription {
    event: string;
    handler: EventListener;
}

/**
 * ReactiveController that subscribes to state events and triggers updates.
 *
 * Usage in a Lit component:
 *
 * ```typescript
 * import { StateController } from '../controllers/state-controller.js';
 * import { StateEvents } from '../../services/state-bridge.js';
 *
 * class MyComponent extends LitElement {
 *   private stateController = new StateController(this);
 *
 *   connectedCallback() {
 *     super.connectedCallback();
 *     this.stateController.subscribe(StateEvents.PLAYERS_UPDATED, (detail) => {
 *       this.players = detail.players;
 *     });
 *   }
 * }
 * ```
 */
export class StateController implements ReactiveController {
    private host: ReactiveControllerHost;
    private subscriptions: Subscription[] = [];

    constructor(host: ReactiveControllerHost) {
        this.host = host;
        host.addController(this);
    }

    hostConnected(): void {
        // Subscriptions are managed via subscribe() calls
    }

    hostDisconnected(): void {
        // Clean up all subscriptions
        for (const sub of this.subscriptions) {
            window.removeEventListener(sub.event, sub.handler);
        }
        this.subscriptions = [];
    }

    /**
     * Subscribe to a state event. The handler receives the event detail.
     * Automatically triggers host.requestUpdate() after the handler runs.
     */
    subscribe<T>(event: string, handler: (detail: T) => void): void {
        const wrappedHandler = (e: Event) => {
            handler((e as CustomEvent<T>).detail);
            this.host.requestUpdate();
        };

        window.addEventListener(event, wrappedHandler);
        this.subscriptions.push({ event, handler: wrappedHandler });
    }

    /**
     * Subscribe to multiple events with a single handler
     */
    subscribeMultiple<T>(events: string[], handler: (detail: T) => void): void {
        for (const event of events) {
            this.subscribe(event, handler);
        }
    }
}
